// ─── seo/structuredData.js ───────────────────────────────────────────────────
// schema.org JSON-LD builders. These tell Google what an entity *is* rather
// than making it infer from prose, which is what makes a page eligible for
// rich results — star ratings on profiles, course cards, and the dedicated
// jobs experience in Google Search.
//
// Each builder tolerates missing fields: schema.org properties are optional,
// and emitting a half-filled object beats emitting none. Undefined keys are
// stripped so we never publish `"author": null`.
import { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE } from './siteMeta';

// JSON.stringify keeps `undefined` out of objects but not out of arrays, and
// empty strings still publish as empty. Strip both.
function clean(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
}

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description:
      'A freelance marketplace for verified university students and alumni, with a ' +
      'transparent 15% total service fee.',
  };
}

// Powers the sitelinks search box and tells Google how to query the site.
export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/browse-freelancers?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function freelancerSchema(user) {
  if (!user) return null;
  const profile = user.freelancerProfile || {};
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const rating = profile.averageRating ?? user.averageRating;
  const reviewCount = profile.reviewCount ?? user.reviewCount;

  return clean({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: name || user.username,
    url: `${SITE_URL}/freelancer/${user._id}`,
    image: user.profilePhoto || undefined,
    description: profile.bio ? stripTags(profile.bio).slice(0, 300) : undefined,
    jobTitle: profile.title || undefined,
    knowsAbout: Array.isArray(profile.skills) && profile.skills.length ? profile.skills : undefined,
    affiliation: profile.university
      ? { '@type': 'CollegeOrUniversity', name: profile.university }
      : undefined,
    // Only emit a rating when there is a real review behind it — fabricating
    // aggregateRating is a manual-action risk with Google.
    aggregateRating:
      Number(reviewCount) > 0 && Number(rating) > 0
        ? {
            '@type': 'AggregateRating',
            ratingValue: Number(rating).toFixed(1),
            reviewCount: Number(reviewCount),
          }
        : undefined,
  });
}

export function courseSchema(course) {
  if (!course) return null;
  const price = Number(course.priceAmount ?? course.pricing?.amount ?? 0);

  return clean({
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    url: `${SITE_URL}/academy/courses/${course._id}`,
    description: course.description ? stripTags(course.description).slice(0, 300) : undefined,
    image: course.thumbnail || course.coverImage || undefined,
    provider: { '@type': 'Organization', name: SITE_NAME, sameAs: SITE_URL },
    // Google's Course rich result requires at least one instance description.
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: course.duration || 'PT2H',
    },
    offers: {
      '@type': 'Offer',
      price: price.toFixed(2),
      priceCurrency: 'USD',
      category: price > 0 ? 'Paid' : 'Free',
      availability: 'https://schema.org/InStock',
    },
  });
}

// `validThrough` is published only when the client actually set a closing date
// on the posting form, where it's optional. Search Console emails about the
// ones without it: leave those alone. Google's own guidance is "If a job
// posting never expires, or you do not know when the job will expire, do not
// include this property", and most postings here genuinely don't expire — a
// client closes one when they've hired, which the status check below handles.
// Deriving a fake expiry (posted + 90 days or similar) would silence the
// warning at the cost of dropping still-open postings out of Google.
export function jobPostingSchema(job) {
  if (!job) return null;

  // A filled or withdrawn posting must not stay in the jobs experience.
  // Removing the structured data is one of the three ways Google documents
  // for taking an expired posting down, and the only one that doesn't need
  // the page itself to start 404ing. Drafts were never public.
  if (job.status && job.status !== 'open') return null;

  return clean({
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    url: `${SITE_URL}/job-postings/${job._id}`,
    description: job.description ? stripTags(job.description).slice(0, 600) : undefined,
    datePosted: job.publishedAt || job.createdAt || undefined,
    validThrough: job.deadline || undefined,
    employmentType: job.budgetType === 'hourly' ? 'PART_TIME' : 'CONTRACTOR',
    hiringOrganization: {
      '@type': 'Organization',
      name: job.companyName || job.postedByName || SITE_NAME,
      sameAs: SITE_URL,
    },
    jobLocationType: 'TELECOMMUTE',
    applicantLocationRequirements: { '@type': 'Country', name: 'USA' },
    // A negotiable job's `budget` is only the floor (budgetMax is the
    // ceiling) — schema.org's QuantitativeValue supports minValue/maxValue
    // for exactly this, so search results don't present the floor as a flat
    // rate the way a single `value` would.
    baseSalary: Number(job.budget) > 0
      ? {
          '@type': 'MonetaryAmount',
          currency: 'USD',
          value: job.budgetType === 'negotiable' && Number(job.budgetMax) > Number(job.budget)
            ? {
                '@type': 'QuantitativeValue',
                minValue: Number(job.budget),
                maxValue: Number(job.budgetMax),
                unitText: 'PROJECT',
              }
            : {
                '@type': 'QuantitativeValue',
                value: Number(job.budget),
                unitText: job.budgetType === 'hourly' ? 'HOUR' : 'PROJECT',
              },
        }
      : undefined,
  });
}

// Descriptions are stored as rich text in places; schema.org wants plain text.
function stripTags(html) {
  return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Makes an already-written on-page FAQ accordion eligible for Google's
// expandable FAQ rich result. `items` is [{ q, a }] — the same shape the
// FindWork/PostAJob FAQ accordions already use, so pages pass their existing
// array straight in rather than duplicating the copy.
export function faqSchema(items) {
  if (!Array.isArray(items) || !items.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: stripTags(a) },
    })),
  };
}
